import { actions, listsV2 } from '@ecosystem/newron-design-system';
import React, { useMemo } from 'react';

import { SelectedRows, TableRow } from '../types';

const { Chips } = actions;

interface Chip {
  id: string;
  primaryLabel: string;
  secondaryLabel?: string;
}

export interface SelectionBodyProps {
  selectedRows: SelectedRows;
  rowSelectionMode: listsV2.RowSelectionMode;
  rowIdToData: Record<string, TableRow>;
  onChipDelete: (rowId: string) => void;
}

const mapSelectedRowsToChips = (selectedRows: SelectedRows, tableMapping: Record<string, TableRow>): Chip[] => {
  return Object.keys(selectedRows).reduce<Chip[]>((chips, rowId) => {
    if (!selectedRows[rowId]) return chips; // Skip if row is not selected

    const row = tableMapping[rowId];
    const hasLabels = !!row?.primaryLabel || !!row?.secondaryLabel;

    if (!hasLabels) {
      // If no labels, use rowId as value
      chips.push({ id: rowId, primaryLabel: rowId });
      return chips;
    }

    chips.push({
      id: rowId,
      primaryLabel: row?.primaryLabel || '',
      secondaryLabel: row?.secondaryLabel,
    });
    return chips;
  }, []);
};

export const SelectionBody: React.FC<SelectionBodyProps> = ({
  selectedRows,
  rowSelectionMode,
  onChipDelete,
  rowIdToData,
}) => {
  const chips = useMemo(() => mapSelectedRowsToChips(selectedRows, rowIdToData), [selectedRows, rowIdToData]);
  const isSingleSelectMode = rowSelectionMode === listsV2.RowSelectionMode.Single;
  const isDataAvailable = Object.keys(rowIdToData).length > 0;

  if (!isDataAvailable) return <div />;
  return (
    <div>
      <Chips.List>
        {(chips ?? []).map(({ id, primaryLabel, secondaryLabel }) => (
          <Chips.Chip
            key={id}
            value={primaryLabel}
            label={secondaryLabel}
            isRemoveButtonDisabled={isSingleSelectMode}
            onRemove={!isSingleSelectMode ? () => onChipDelete(id) : undefined}
          />
        ))}
      </Chips.List>
    </div>
  );
};
